import { runInAction } from 'mobx';
import { toast } from 'sonner';

import { isNewNoteDisabled } from '@/features/meetings/meetings.hooks';
import { meetingApis } from '@/features/meetings/meetings.query';
import { meetingStore } from '@/features/meetings/meetings.state';
import { Routes } from '@/types/routes';
import { AppCapability, isCapable } from '@/utils/capabilities';
import { navigate } from '@/utils/navigation-service';

export const canRecordMeetings = () => isCapable(AppCapability.RecordMeeting);

export const startNewChat = () => {
  navigate({ to: Routes.Chats as string, search: { new: true } });
};

export const isMeetingNoteStartBlocked = () =>
  isNewNoteDisabled() || meetingStore.controlBarLoading;

export const startMeetingNote = async () => {
  if (isMeetingNoteStartBlocked()) return;

  const setStarting = (starting: boolean) =>
    runInAction(() => {
      meetingStore.setIsStartingMeeting(starting);
      meetingStore.controlBarLoading = starting;
    });

  setStarting(true);

  try {
    await meetingApis.enqueueStartMeeting({
      payload: { startReason: 'manual' },
    });
  } catch {
    toast.error('Could not start the meeting note');
  } finally {
    setStarting(false);
  }
};

export const stopMeetingNote = async () => {
  meetingStore.closeTranscriptPopover();

  try {
    await meetingApis.enqueueStopMeeting(
      { type: 'stop_note_taking' },
      { deferSummary: true }
    );
  } catch {
    toast.error('Could not stop the meeting note');
  }
};

export const openMeetingNote = (meetingId: string) => {
  navigate({
    to: '/meetings/$meetingId',
    params: { meetingId },
    search: { tab: 'notes' },
  });
};

export const formatElapsed = (ms: number) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`;
  }
  return `${minutes}:${seconds}`;
};
